import { Link } from "react-router-dom";

import { StatusPill } from "@/components/admin/status-pill";
import { DeadlineCell } from "@/components/admin/deadline-cell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LocalDateTime } from "@/components/ui/local-datetime";
import {
  PACKAGE_LABEL,
  isOverdue,
  type OrdersListResult,
} from "@/lib/admin-orders-shared";
import { formatPrice } from "@/lib/packages";
import { cn } from "@/lib/utils";

export function OrdersTable({ result, loading }: { result: OrdersListResult | null; loading?: boolean }) {
  if (loading || !result) {
    return (
      <div className="rounded-lg p-10 text-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (result.rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-10 text-center text-sm text-muted-foreground">
        No orders match these filters.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[120px]">Order</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Package</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Deadline</TableHead>
          <TableHead className="text-right">Price</TableHead>
          <TableHead className="text-right">Submitted</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {result.rows.map((order) => {
          const overdue = isOverdue(order);
          return (
            <TableRow
              key={order.id}
              className={cn(
                "group cursor-pointer",
                overdue && "bg-destructive/[0.04] hover:bg-destructive/[0.08]",
              )}
            >
              <TableCell className="font-mono text-xs">
                <Link
                  to={`/admin/orders/${order.id}`}
                  className="block w-full underline-offset-4 hover:underline"
                >
                  {order.orderNumber}
                </Link>
              </TableCell>
              <TableCell>
                <Link to={`/admin/orders/${order.id}`} className="block min-w-0">
                  <div className="truncate font-medium">{order.businessName}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {order.contactName} · {order.contactEmail}
                  </div>
                </Link>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {PACKAGE_LABEL[order.packageType]}
              </TableCell>
              <TableCell>
                <StatusPill status={order.status} />
              </TableCell>
              <TableCell>
                {order.paidAt ? (
                  <DeadlineCell iso={order.deadlineAt} overdue={overdue} />
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right text-xs font-medium">
                {formatPrice(order.priceUsd)}
              </TableCell>
              <TableCell className="text-right text-xs text-muted-foreground">
                <LocalDateTime iso={order.createdAt} preset="medium" />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
